import { LanguageCode } from "@/lib/languages";
import dict from "@/data/uiTranslations.json";
import { EXTRA_UI_TRANSLATIONS } from "@/data/extraUiTranslations";
import { supabase } from "@/integrations/supabase/client";


/**
 * Static-dictionary DOM translator. No network calls.
 *
 * Walks the document, finds visible text nodes + a few translatable attributes,
 * stores the original English on the node, and replaces the displayed value
 * using the bundled translations in `src/data/uiTranslations.json`.
 *
 * Strings missing from the dictionary are left in English.
 * Elements (or any of their ancestors) marked `data-no-translate="true"`
 * are skipped entirely — used for the JJT panel, support/group/admin chats,
 * and Game Center / individual games.
 */

const BASE_TRANSLATIONS = dict as Record<string, Record<string, string>>;
const TRANSLATIONS = Object.fromEntries(
  Array.from(new Set([...Object.keys(BASE_TRANSLATIONS), ...Object.keys(EXTRA_UI_TRANSLATIONS)])).map((lang) => [
    lang,
    {
      ...(BASE_TRANSLATIONS[lang] || {}),
      ...(EXTRA_UI_TRANSLATIONS[lang] || {}),
    },
  ]),
) as Record<string, Record<string, string>>;

/* ---------- runtime (AI) dictionary: fills whatever the static dict misses ---------- */

const RUNTIME_KEY = (lang: string) => `ss_i18n_cache_${lang}`;
const runtime: Record<string, Record<string, string>> = {};
const missing = new Set<string>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let inFlight = 0;
/** Omitted model responses are not retried; transient request failures get bounded retries. */
const failed = new Set<string>();
const retryCount = new Map<string, number>();
const MAX_RETRIES = 3;

function loadRuntime(lang: string): Record<string, string> {
  if (runtime[lang]) return runtime[lang];
  let parsed: Record<string, string> = {};
  try {
    const raw = localStorage.getItem(RUNTIME_KEY(lang));
    if (raw) parsed = JSON.parse(raw) || {};
  } catch {}
  runtime[lang] = parsed;
  return parsed;
}

function saveRuntime(lang: string) {
  try { localStorage.setItem(RUNTIME_KEY(lang), JSON.stringify(runtime[lang] || {})); } catch {}
}

function lookup(lang: string, text: string, allowRuntime = false): string | undefined {
  const stat = TRANSLATIONS[lang]?.[text];
  if (stat) return stat;
  const rt = loadRuntime(lang)[text];
  if (rt) return rt;
  if (
    allowRuntime &&
    !failed.has(`${lang}|${text}`) &&
    text.length <= 220
  ) queueMissing(lang, text);
  return undefined;
}

function queueMissing(lang: string, text: string) {
  missing.add(text);
  if (flushTimer) return;
  flushTimer = setTimeout(() => { flushTimer = null; void flushMissing(lang); }, 400);
}

async function flushMissing(lang: string) {
  if (lang === "en" || lang !== currentLang) { missing.clear(); return; }
  if (inFlight >= 2) { setTimeout(() => void flushMissing(lang), 800); return; }
  const batch = Array.from(missing).slice(0, 60);
  if (!batch.length) return;
  batch.forEach((b) => missing.delete(b));
  inFlight++;
  try {
    const { data, error } = await supabase.functions.invoke("translate-ui", {
      body: { language: lang, texts: batch },
    });
    if (error) throw error;
    const translations = (data as any)?.translations as Record<string, string> | undefined;
    const store = loadRuntime(lang);
    let added = 0;
    for (const key of batch) {
      const v = translations?.[key];
      if (v) { store[key] = v; added++; }
      else failed.add(`${lang}|${key}`);
    }
    if (added) {
      saveRuntime(lang);
      if (lang === currentLang && document.body) applyAll(document.body);
    }
  } catch (e) {
    // Retry transient failures, but cap attempts so an outage cannot hammer the function.
    for (const text of batch) {
      const key = `${lang}|${text}`;
      const attempts = (retryCount.get(key) || 0) + 1;
      retryCount.set(key, attempts);
      if (attempts < MAX_RETRIES) missing.add(text);
      else failed.add(key);
    }
    console.warn("[domTranslator] runtime translation failed", e);
  } finally {
    inFlight--;
    if (missing.size && !flushTimer) {
      flushTimer = setTimeout(() => { flushTimer = null; void flushMissing(lang); }, 1200);
    }
  }
}


const ORIG_TEXT = "__ssOrigText";
const ORIG_ATTR_PREFIX = "__ssOrigAttr_";
const TRANSLATABLE_ATTRS = ["placeholder", "title", "aria-label", "alt"] as const;
const SKIP_TAGS = new Set([
  "SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "SVG", "CANVAS", "TEXTAREA", "INPUT",
]);
const TEXT_CONTENT_SKIP_TAGS = new Set([
  "SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "SVG", "CANVAS", "TEXTAREA", "INPUT",
]);

let currentLang: LanguageCode = "en";
let observer: MutationObserver | null = null;

function shouldSkipElement(el: Element | null): boolean {
  let cur: Element | null = el;
  while (cur) {
    if (SKIP_TAGS.has(cur.tagName)) return true;
    if ((cur as HTMLElement).dataset?.noTranslate === "true") return true;
    if (cur.getAttribute?.("contenteditable") === "true") return true;
    cur = cur.parentElement;
  }
  return false;
}

function isTextWorthTranslating(s: string): boolean {
  const trimmed = s.trim();
  if (trimmed.length < 2) return false;
  if (!/[a-zA-Z]/.test(trimmed)) return false;
  return true;
}

function applyText(node: Text, translated: string) {
  const original = (node as any)[ORIG_TEXT] as string;
  if (!original) return;
  if (!node.isConnected) return;
  const leading = original.match(/^\s*/)?.[0] ?? "";
  const trailing = original.match(/\s*$/)?.[0] ?? "";
  const next = leading + translated.trim() + trailing;
  if (node.nodeValue === next) return;
  try { node.nodeValue = next; } catch {}
}

function collectTextNodes(root: Node, out: Text[]) {
  if (root.nodeType === Node.TEXT_NODE) {
    const tn = root as Text;
    if (!tn.parentElement || shouldSkipElement(tn.parentElement)) return;
    const v = tn.nodeValue || "";
    if (!isTextWorthTranslating(v)) return;
    out.push(tn);
    return;
  }
  if (root.nodeType !== Node.ELEMENT_NODE) return;
  const el = root as Element;
  if (shouldSkipElement(el)) return;
  for (const child of Array.from(root.childNodes)) collectTextNodes(child, out);
}

function collectAttrTargets(root: Node, out: Array<{ el: Element; attr: string }>) {
  if (root.nodeType !== Node.ELEMENT_NODE) return;
  const el = root as Element;
  const isAttrSafeFormControl = el.tagName === "INPUT" || el.tagName === "TEXTAREA";
  const skipAttrs = isAttrSafeFormControl ? shouldSkipTranslatableAttrs(el) : shouldSkipElement(el);
  if (!skipAttrs) {
    for (const attr of TRANSLATABLE_ATTRS) {
      const v = el.getAttribute(attr);
      if (v && isTextWorthTranslating(v)) out.push({ el, attr });
    }
  }
  for (const child of Array.from(el.children)) collectAttrTargets(child, out);
}

function shouldSkipTranslatableAttrs(el: Element): boolean {
  let cur: Element | null = el;
  while (cur) {
    if ((cur as HTMLElement).dataset?.noTranslate === "true") return true;
    if (cur.getAttribute?.("contenteditable") === "true") return true;
    if (cur !== el && SKIP_TAGS.has(cur.tagName)) return true;
    cur = cur.parentElement;
  }
  return false;
}

function ensureOriginalText(node: Text): string {
  const stored = (node as any)[ORIG_TEXT];
  if (stored) return stored;
  const v = node.nodeValue || "";
  (node as any)[ORIG_TEXT] = v;
  return v;
}

function ensureOriginalAttr(el: Element, attr: string): string {
  const key = ORIG_ATTR_PREFIX + attr;
  const stored = (el as any)[key];
  if (stored) return stored;
  const v = el.getAttribute(attr) || "";
  (el as any)[key] = v;
  return v;
}

function applyAllImpl(rootNode: Node = document.body) {
  if (!rootNode) return;
  if (rootNode.nodeType === Node.ELEMENT_NODE && !(rootNode as Element).isConnected) return;
  const lang = currentLang;

  const wasMuted = muteObserver;
  muteObserver = true;
  try {
    const textNodes: Text[] = [];
    collectTextNodes(rootNode, textNodes);
    for (const tn of textNodes) {
      if (!tn.isConnected) continue;
      const orig = ensureOriginalText(tn);
      if (lang === "en") {
        if (tn.nodeValue !== orig) { try { tn.nodeValue = orig; } catch {} }
        continue;
      }
      const trimmed = orig.trim();
      // Nodes inside user-content surfaces are excluded by shouldSkipElement;
      // everything else is application copy and can fill dictionary gaps at runtime.
      const t = lookup(lang, trimmed, true);
      if (t) applyText(tn, t);
      else if (tn.nodeValue !== orig) { try { tn.nodeValue = orig; } catch {} }
    }

    const attrTargets: Array<{ el: Element; attr: string }> = [];
    collectAttrTargets(rootNode, attrTargets);
    for (const { el, attr } of attrTargets) {
      if (!el.isConnected) continue;
      const orig = ensureOriginalAttr(el, attr);
      if (lang === "en") {
        if (el.getAttribute(attr) !== orig) { try { el.setAttribute(attr, orig); } catch {} }
        continue;
      }
      const trimmed = orig.trim();
      const t = lookup(lang, trimmed, true);
      if (t) { try { el.setAttribute(attr, t); } catch {} }
      else if (el.getAttribute(attr) !== orig) { try { el.setAttribute(attr, orig); } catch {} }
    }

  } catch (e) {
    console.warn("[domTranslator] applyAll failed", e);
  } finally {
    muteObserver = wasMuted;
  }
}

let applyScheduled = false;
const pendingRoots = new Set<Node>();
function applyAll(rootNode: Node = document.body) {
  pendingRoots.add(rootNode);
  if (applyScheduled) return;
  applyScheduled = true;
  requestAnimationFrame(() => {
    applyScheduled = false;
    const roots = Array.from(pendingRoots);
    pendingRoots.clear();
    for (const r of roots) {
      try { applyAllImpl(r); } catch (e) { console.warn("[domTranslator] apply failed", e); }
    }
  });
}

let muteObserver = false;
function startObserver() {
  if (observer) return;
  observer = new MutationObserver((mutations) => {
    if (muteObserver) return;
    muteObserver = true;
    try {
      for (const m of mutations) {
        if (m.type === "childList") {
          for (const n of Array.from(m.addedNodes)) {
            if (n.nodeType === Node.ELEMENT_NODE || n.nodeType === Node.TEXT_NODE) {
              applyAll(n);
            }
          }
        } else if (m.type === "characterData") {
          const tn = m.target as Text;
          if (!tn.parentElement || shouldSkipElement(tn.parentElement)) continue;
          (tn as any)[ORIG_TEXT] = tn.nodeValue || "";
          if (currentLang !== "en") applyAll(tn);
        } else if (m.type === "attributes" && m.attributeName) {
          const attr = m.attributeName;
          if ((TRANSLATABLE_ATTRS as readonly string[]).includes(attr)) {
            const el = m.target as Element;
            (el as any)[ORIG_ATTR_PREFIX + attr] = el.getAttribute(attr) || "";
            applyAll(el);
          }
        }
      }
    } finally {
      muteObserver = false;
    }
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: [...TRANSLATABLE_ATTRS],
  });
}

export function setupDomTranslator(_opts: { onTranslatingChange?: (b: boolean) => void } = {}) {
  startObserver();
  return () => {
    observer?.disconnect();
    observer = null;
  };
}

export function setDomTranslatorLanguage(lang: LanguageCode) {
  currentLang = lang;
  missing.clear();
  if (document.body) applyAll(document.body);
}

/** Static + runtime (AI) lookup for imperative strings, e.g. the `t()` helper. */
export function translateString(lang: LanguageCode, text: string): string {
  if (!text || lang === "en") return text;
  return lookup(lang, text.trim(), true) ?? text;
}

